import { isNil, isUndefined, Dictionary } from 'lodash';
import { Identifiable, Modifiable, Nullable, OmitByType, omitDeepUndefined, Ownable } from '../../utils';
import { AssignmentType } from './AssignmentComplete';

// TODO fix
interface NavigationStateProgress {
  current: number;
  total: number;
}

export type AssignmentSettingsType<T extends AssignmentType> = T extends 'realtime'
  ? AssignmentSettings.Realtime
  : AssignmentSettings;

export type AssignmentSettings = AssignmentSettings.Realtime;

// TODO: this should be migrated to db-assignment-settings.ts and use Zod types instead
export namespace AssignmentSettings {
  export const excludedComparisonPaths: string[] = ['createdAt', 'modifiedAt'];

  export type Status = 'open' | 'closed';
  export type Visibility = 'all' | 'studentOnly';

  type CreationIgnoredTypes = Ownable & Identifiable & Modifiable;

  export type RealtimeCreationSettings = OmitByType<Realtime, CreationIgnoredTypes>;

  export type CreationSettings = RealtimeCreationSettings;
  export type CreationSettingsTyped<T extends AssignmentType> = T extends 'realtime'
    ? RealtimeCreationSettings
    : CreationSettings;

  // settings for all
  interface BaseSettings extends Ownable, Identifiable, Modifiable {
    status: Status;
    visibility: Visibility;
  }

  // this is a object to make room for future settings
  export interface ShowFinalSlideSettings {
    showSlide: boolean;
  }

  export interface Realtime extends BaseSettings {
    type: 'realtime';
    nextAssignmentId?: string;
    nextAssignmentVersion?: 'v2';
    currentView: View;
    pincode: string | undefined;
    playerSettings: RealtimePlayerSettings;
  }

  export interface RealtimePlayerSettings {
    isSoundEnabled: boolean;
    allowConnectedDevices: boolean;
    isScreenShared: boolean;
    isPrepScreenEnabled: boolean;
    allowNewPlayers: boolean;
    autoShowRank: boolean;
    ignoreExclusions: boolean;
  }

  /**
   * key's are ${pinId}-${paramName}
   * value are just the normal values
   */
  export interface QueryData {
    [pinIdParam: string]: string;
  }

  export interface View {
    pinId: string; // master pin
    foregroundPinId: string;
    params: QueryData | undefined;
    hasNext: boolean | undefined;
    hasPrevious: boolean | undefined;
    progress: NavigationStateProgress | undefined;
  }
}

export namespace AssignmentSettings {
  export function create<T extends AssignmentType = AssignmentType>(
    assignmentId: string,
    ownerId: string,
    createdAt: number,
    creationSettings: CreationSettingsTyped<T>
  ): AssignmentSettingsType<T> {
    const settings: AssignmentSettingsType<T> = {
      ...creationSettings,
      createdAt,
      modifiedAt: createdAt,
      ownerId,
      _id: assignmentId,
    } as AssignmentSettingsType<T>;
    return settings;
  }

  export function realtimeModeView(settings: AssignmentSettings | undefined): View | undefined {
    if (!settings || settings.type !== 'realtime' || isNil(settings.currentView)) {
      return undefined;
    }

    return settings.currentView;
  }
}

export namespace AssignmentSettings {
  interface RealtimeUpdateData
    extends Nullable<
      Pick<
        AssignmentSettings.Realtime,
        'currentView' | 'nextAssignmentId' | 'nextAssignmentVersion' | 'pincode' | 'status'
      >
    > {
    playerSettings?: Nullable<Partial<RealtimePlayerSettings>>; // TODO: Remove this comment after migration, changed this to support GraphQL type
    params?: QueryData;
  }

  export type UpdateData = Partial<RealtimeUpdateData>;

  export function updateFields(data: UpdateData): object {
    const updateFields: Dictionary<any> = {};

    if (!isUndefined(data.currentView)) updateFields['currentView'] = data.currentView;
    if (!isUndefined(data.params)) updateFields['currentView.params'] = data.params;
    if (!isUndefined(data.status)) updateFields['status'] = data.status;
    if (!isUndefined(data.pincode)) updateFields['pincode'] = data.pincode;
    if (!isUndefined(data.nextAssignmentId)) updateFields['nextAssignmentId'] = data.nextAssignmentId;
    if (!isUndefined(data.nextAssignmentVersion)) updateFields['nextAssignmentVersion'] = data.nextAssignmentVersion;

    if (data.playerSettings) {
      const {
        isScreenShared,
        isSoundEnabled,
        allowConnectedDevices,
        isPrepScreenEnabled,
        allowNewPlayers,
        autoShowRank,
        ignoreExclusions,
      } = data.playerSettings;
      if (!isNil(isScreenShared)) updateFields['playerSettings.isScreenShared'] = isScreenShared;
      if (!isNil(ignoreExclusions)) updateFields['playerSettings.ignoreExclusions'] = ignoreExclusions;
      if (!isNil(isSoundEnabled)) updateFields['playerSettings.isSoundEnabled'] = isSoundEnabled;
      if (!isNil(allowConnectedDevices)) updateFields['playerSettings.allowConnectedDevices'] = allowConnectedDevices;
      if (!isNil(allowNewPlayers)) updateFields['playerSettings.allowNewPlayers'] = allowNewPlayers;
      if (!isNil(isPrepScreenEnabled)) updateFields['playerSettings.isPrepScreenEnabled'] = isPrepScreenEnabled;
      if (!isNil(autoShowRank)) updateFields['playerSettings.autoShowRank'] = autoShowRank;
    }

    const cleaned = omitDeepUndefined(updateFields);
    if (!isNil(cleaned)) {
      cleaned['modifiedAt'] = Date.now();
    }

    return cleaned;
  }
}
